import {useState} from 'react'
import {useNavigate, useParams, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {PageHeading} from '@/components/page-heading'
import {Tag} from '@/components/ui/tag'
import {Skeleton} from '@/components/ui/skeleton'
import {Textarea} from '@/components/ui/textarea'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {MicroLabel} from '@/components/period-selector'
import {Hint} from '@/components/ui/hint'
import {HelpIcon} from '@/components/help-icon'
import {PorQue} from '@/components/ui/por-que'
import {FieldError} from '@/components/field-error'
import {useOperatorPage} from '@/components/no-access-state'
import {CertificacionTab} from '@/components/certificacion-tab'
import {ActividadDetalleDialog} from '@/components/plan-trabajo-actividad-dialog'
import {Cronograma} from '@/components/plan-trabajo-cronograma'
import {EmpleadoSelect} from '@/components/plan-trabajo-empleado-select'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useResponsablesRoster, type Employee} from '@/hooks/empresa/identidad/use-employees'
import {
  useCrearAdenda,
  useCrearPlan,
  useEditarEncabezado,
  useFirmarPlan,
  usePlanTrabajo,
  useProponerPlan,
  type ActividadCrear,
  type ActividadPropuesta,
  type PlanTrabajo,
} from '@/hooks/sgsst/plan/use-plan-trabajo'
import {downloadBlob} from '@/lib/api'
import {translateApiError} from '@/lib/errors'
import {validateRequired} from '@/lib/validation'
import {LockGlyph} from '@/components/ui/icons'
import {cn} from '@/lib/utils'
import {tope} from '@/lib/campos-texto'

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

type TabKey = 'plan' | 'certificacion'

function parseTab(value: string | null): TabKey | null {
  return value === 'plan' || value === 'certificacion' ? value : null
}

/** Plan anual (Res. 0312/2019 art. 28), el único candado duro (UX O5): se compila de las fuentes del
 * sistema, nunca se teclea a mano. */
export function PlanTrabajoPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const {business, guard} = useOperatorPage(businessId)
  const {data: estado, isLoading} = usePlanTrabajo(businessId)
  const [searchParams, setSearchParams] = useSearchParams()
  if (guard) return guard
  if (!business) return null

  const tab = parseTab(searchParams.get('tab')) ?? 'plan'
  function setTab(next: TabKey) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.set('tab', next)
        return params
      },
      {replace: true}
    )
  }

  return (
    <div className="space-y-6">
      <PageHeading title={t('planTrabajo.page.title')} subtitle={business.name} />
      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList>
          <TabsTrigger value="plan" data-testid="tab-plan">
            {t('planTrabajo.tabs.plan')}
          </TabsTrigger>
          <TabsTrigger value="certificacion" data-testid="tab-certificacion">
            {t('planTrabajo.tabs.certificacion')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="plan" className="pt-4">
          {isLoading || !estado ? (
            <Skeleton className="h-40 w-full" />
          ) : estado.plan ? (
            <PlanView businessId={business.id} plan={estado.plan} />
          ) : (
            <EmptyState businessId={business.id} year={estado.period_year} puedeProponer={estado.puede_proponer} />
          )}
        </TabsContent>

        <TabsContent value="certificacion" className="pt-4">
          <CertificacionTab businessId={business.id} artefacto="plan_trabajo" />
        </TabsContent>
      </Tabs>
    </div>
  )
}

function EmptyState({businessId, year, puedeProponer}: {businessId: string; year: number; puedeProponer: boolean}) {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const [proponiendo, setProponiendo] = useState(false)

  if (proponiendo) {
    return <ProponerFlow businessId={businessId} year={year} onCancel={() => setProponiendo(false)} />
  }

  if (!puedeProponer) {
    return (
      <div
        className="surface-raised mx-auto flex max-w-lg flex-col items-center gap-4 p-8 text-center"
        data-testid="plan-trabajo-locked">
        <span aria-hidden className="flex h-11 w-11 items-center justify-center rounded-full bg-accent text-primary">
          <LockGlyph size={20} />
        </span>
        <Tag tone="neutral">{t('planTrabajo.page.badge')}</Tag>
        <h2 className="font-heading text-lg text-foreground">{t('planTrabajo.page.headline')}</h2>
        <PorQue
          label={t('planTrabajo.page.porque')}
          testId="plan-trabajo-porque"
          contenidoTestId="plan-trabajo-porque-contenido">
          {t('planTrabajo.page.porqueCuerpo')}
        </PorQue>
        <Button data-testid="plan-trabajo-cta" onClick={() => navigate(`/businesses/${businessId}/sgsst/plan`)}>
          {t('planTrabajo.page.cta')}
        </Button>
      </div>
    )
  }

  return (
    <div
      className="surface-raised mx-auto flex max-w-lg flex-col items-center gap-4 p-8 text-center"
      data-testid="plan-trabajo-empty">
      <h2 className="font-heading text-lg text-foreground">{t('planTrabajo.empty.headline')}</h2>
      <Button data-testid="proponer-plan-cta" onClick={() => setProponiendo(true)}>
        {t('planTrabajo.empty.cta', {year})}
      </Button>
    </div>
  )
}

type RowState = ActividadPropuesta & {asignado_employee_id: string | null; incluida: boolean}

function ProponerFlow({businessId, year, onCancel}: {businessId: string; year: number; onCancel: () => void}) {
  const {t} = useTranslation()
  const [tienePlan, setTienePlan] = useState<boolean | null>(null)
  const proponer = useProponerPlan(businessId)
  const crear = useCrearPlan(businessId)
  const {data: roster} = useResponsablesRoster(businessId, true)
  const [rows, setRows] = useState<RowState[] | null>(null)
  const [subirSoporte, setSubirSoporte] = useState<string | null>(null)

  async function compilar(tiene: boolean) {
    setTienePlan(tiene)
    const res = await proponer.mutateAsync(tiene)
    setSubirSoporte(res.subir_a_soporte_key)
    setRows(res.actividades.map((a) => ({...a, asignado_employee_id: null, incluida: true})))
  }

  if (tienePlan === null) {
    return (
      <div className="surface-raised mx-auto max-w-lg space-y-4 p-8 text-center" data-testid="proponer-pregunta">
        <div className="flex items-center justify-center gap-1.5">
          <h2 className="font-heading text-lg text-foreground">{t('planTrabajo.pregunta.titulo', {year})}</h2>
          <HelpIcon
            label={t('common.help.iconLabelFor', {campo: t('planTrabajo.pregunta.titulo', {year})})}
            testId="pregunta-tip">
            {t('planTrabajo.pregunta.tip')}
          </HelpIcon>
        </div>
        <div className="flex justify-center gap-3">
          <Button variant="outline" data-testid="pregunta-si" onClick={() => void compilar(true)}>
            {t('planTrabajo.pregunta.si')}
          </Button>
          <Button data-testid="pregunta-no" onClick={() => void compilar(false)}>
            {t('planTrabajo.pregunta.no')}
          </Button>
        </div>
        <button type="button" className="text-sm text-muted-foreground underline" onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </div>
    )
  }

  if (!rows) {
    return <Skeleton className="h-40 w-full" />
  }

  const incluidas = rows.filter((r) => r.incluida)

  async function confirmar() {
    const actividades: ActividadCrear[] = incluidas.map((r) => ({
      titulo: r.titulo,
      objetivo: r.objetivo,
      meta: r.meta,
      fuente: r.fuente,
      referencia: r.referencia,
      cita: r.cita,
      meses_planeados: r.meses_planeados,
      asignado_employee_id: r.asignado_employee_id,
    }))
    await crear.mutateAsync(actividades)
  }

  return (
    <div className="surface overflow-hidden" data-testid="propuesta">
      <div className="surface-divider border-b px-4 py-3.5">
        <h2 className="font-heading text-lg text-foreground">{t('planTrabajo.propuesta.titulo', {year})}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('planTrabajo.propuesta.nadaSeCrea')}</p>
        {subirSoporte && (
          <p className="mt-2 text-sm text-primary" data-testid="propuesta-subir-soporte">
            {t('planTrabajo.propuesta.subirExistente')}
          </p>
        )}
      </div>

      <div className="px-4">
        {rows.map((row, i) => (
          <PropuestaRow
            key={row.clave}
            row={row}
            roster={roster ?? []}
            onToggle={() => setRows(rows.map((r, j) => (j === i ? {...r, incluida: !r.incluida} : r)))}
            onAsignar={(id) => setRows(rows.map((r, j) => (j === i ? {...r, asignado_employee_id: id} : r)))}
          />
        ))}
      </div>

      <div
        className="surface-divider flex items-center justify-between gap-3 border-t bg-secondary/40 px-4 py-3"
        data-testid="propuesta-footer">
        <span className="text-sm text-muted-foreground">
          {t('planTrabajo.propuesta.seleccionadas', {n: incluidas.length, total: rows.length})}
        </span>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button
            data-testid="propuesta-crear"
            disabled={incluidas.length === 0 || crear.isPending}
            onClick={() => void confirmar()}>
            {t('planTrabajo.propuesta.crear', {n: incluidas.length})}
          </Button>
        </div>
      </div>
    </div>
  )
}

function PropuestaRow({
  row,
  roster,
  onToggle,
  onAsignar,
}: {
  row: RowState
  roster: Employee[]
  onToggle: () => void
  onAsignar: (id: string | null) => void
}) {
  const {t} = useTranslation()
  return (
    <div
      className={cn(
        '-mx-4 border-b border-border px-4 py-3.5 transition-colors last:border-b-0 hover:bg-secondary/30',
        !row.incluida && 'opacity-50'
      )}
      data-testid="propuesta-fila">
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={row.incluida}
          onChange={onToggle}
          data-testid="propuesta-check"
          aria-label={row.titulo}
          className="mt-1 h-4 w-4"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Tag tone="neutral">{t(`planTrabajo.fuente.${row.fuente}`)}</Tag>
            <p className="text-[15px] font-medium text-foreground">{row.titulo}</p>
          </div>
          <p className="mt-1 text-[13.5px] text-muted-foreground">{row.objetivo}</p>
          <p className="mt-0.5 text-[13.5px] text-muted-foreground">
            {t('planTrabajo.propuesta.metaLabel')}: {row.meta}
          </p>
          {row.cita && (
            <p className="mt-1 font-mono text-[11px] text-muted-foreground" data-testid="propuesta-cita">
              {row.cita}
            </p>
          )}
          <div className="mt-2 flex items-center gap-2">
            <MicroLabel>{t('planTrabajo.propuesta.mesesLabel')}</MicroLabel>
            <span className="font-mono text-[12px] tabular-nums text-foreground">
              {row.meses_planeados.map((m) => MESES[m - 1]).join(' · ')}
            </span>
          </div>
          {row.incluida && (
            <div className="mt-2 flex items-center gap-2">
              <MicroLabel>{t('planTrabajo.propuesta.asignar')}</MicroLabel>
              <EmpleadoSelect
                roster={roster}
                value={row.asignado_employee_id}
                placeholder={t('planTrabajo.propuesta.sinAsignar')}
                testId="propuesta-asignado"
                onPick={onAsignar}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function PlanView({businessId, plan}: {businessId: string; plan: PlanTrabajo}) {
  const {t} = useTranslation()
  const firmado = plan.estado === 'firmado'
  const [detalleId, setDetalleId] = useState<string | null>(null)
  const detalle = plan.actividades.find((a) => a.id === detalleId) ?? null

  return (
    <div className="space-y-5" data-testid="plan-trabajo-view">
      <EstadoFranja firmado={firmado} />

      <div className="surface flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="flex items-center gap-3">
          <Tag tone={firmado ? 'green' : 'amber'}>{t(`planTrabajo.estado.${plan.estado}`)}</Tag>
          {plan.version > 1 && (
            <Tag tone="neutral" data-testid="plan-version">
              {t('planTrabajo.adenda.version', {n: plan.version})}
            </Tag>
          )}
          <span className="font-mono text-[13px] tabular-nums text-foreground" data-testid="plan-avance">
            {t('planTrabajo.avance', {pct: plan.avance_pct})}
          </span>
        </div>
        <Button
          variant="outline"
          data-testid="plan-descargar"
          onClick={() =>
            downloadBlob(
              `/businesses/${businessId}/sgsst/plan-trabajo/${plan.id}/documento`,
              `plan-trabajo-${plan.period_year}.docx`
            )
          }>
          {t('planTrabajo.descargar')}
        </Button>
      </div>

      <EncabezadoPlan businessId={businessId} plan={plan} />

      <Cronograma businessId={businessId} plan={plan} onAbrirDetalle={setDetalleId} />
      <div id="firmas-plan">
        <Firmas businessId={businessId} plan={plan} />
      </div>

      {detalle && (
        <ActividadDetalleDialog
          businessId={businessId}
          plan={plan}
          actividad={detalle}
          onClose={() => setDetalleId(null)}
        />
      )}
    </div>
  )
}

function EstadoFranja({firmado}: {firmado: boolean}) {
  const {t} = useTranslation()
  const paso = firmado ? 'firmado' : 'borrador'
  const targetId = firmado ? 'cronograma-plan' : 'firmas-plan'
  return (
    <div
      className="surface flex flex-wrap items-center justify-between gap-3 border-l-4 border-primary p-4"
      data-testid="plan-franja-estado">
      <p className="text-[14px] font-medium text-foreground">{t(`planTrabajo.franja.${paso}.titulo`)}</p>
      <a href={`#${targetId}`} className="text-[13px] font-medium text-primary underline underline-offset-2">
        {t(`planTrabajo.franja.${paso}.cta`)}
      </a>
    </div>
  )
}

const ENCABEZADO_CAMPOS = ['objetivo', 'alcance', 'responsable', 'meta', 'recursos'] as const
type EncabezadoCampo = (typeof ENCABEZADO_CAMPOS)[number]

function valoresIniciales(plan: PlanTrabajo): Record<EncabezadoCampo, string> {
  return Object.fromEntries(ENCABEZADO_CAMPOS.map((c) => [c, plan[c] ?? ''])) as Record<EncabezadoCampo, string>
}

function EncabezadoPlan({businessId, plan}: {businessId: string; plan: PlanTrabajo}) {
  const {t} = useTranslation()
  const editable = plan.estado === 'borrador'
  const editar = useEditarEncabezado(businessId, plan.id)
  const [editando, setEditando] = useState(false)
  const [valores, setValores] = useState<Record<EncabezadoCampo, string>>(() => valoresIniciales(plan))
  const [errores, setErrores] = useState<Partial<Record<EncabezadoCampo, string>>>({})
  const [error, setError] = useState<string | null>(null)
  const [guardado, setGuardado] = useState(false)

  function empezar() {
    setValores(valoresIniciales(plan))
    setErrores({})
    setError(null)
    setEditando(true)
  }

  function cambiar(campo: EncabezadoCampo, valor: string) {
    setValores((v) => ({...v, [campo]: valor}))
    setErrores((e) => ({...e, [campo]: undefined}))
  }

  function validarCampo(campo: EncabezadoCampo) {
    setErrores((e) => ({...e, [campo]: validateRequired(valores[campo]) ?? undefined}))
  }

  async function guardar() {
    const problemas: Partial<Record<EncabezadoCampo, string>> = {}
    for (const campo of ENCABEZADO_CAMPOS) {
      const problema = validateRequired(valores[campo])
      if (problema) problemas[campo] = problema
    }
    setErrores(problemas)
    if (Object.keys(problemas).length > 0) return
    setError(null)
    try {
      await editar.mutateAsync(valores)
      setEditando(false)
      setGuardado(true)
    } catch (err) {
      setError(translateApiError(err))
    }
  }

  return (
    <div className="surface" data-testid="plan-encabezado">
      <div className="surface-divider border-b px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-heading text-base text-foreground">{t('planTrabajo.encabezado.titulo')}</h2>
          {editable && !editando && (
            <button
              type="button"
              className="text-[12px] font-medium text-primary underline"
              data-testid="encabezado-editar"
              onClick={empezar}>
              {t('planTrabajo.encabezado.editar')}
            </button>
          )}
        </div>
        {!editable && (
          <p className="mt-1 text-[12px] text-muted-foreground">{t('planTrabajo.encabezado.soloEnBorrador')}</p>
        )}
        {guardado && !editando && (
          <div className="mt-1.5">
            <Tag tone="green" data-testid="encabezado-guardado">
              {t('planTrabajo.encabezado.guardadoOk')}
            </Tag>
          </div>
        )}
      </div>
      <div className="space-y-4 px-4 py-4">
        {ENCABEZADO_CAMPOS.map((campo) => (
          <div key={campo}>
            <MicroLabel>{t(`planTrabajo.encabezado.${campo}`)}</MicroLabel>
            {editando ? (
              <>
                <Textarea
                  maxLength={tope('NOTA')}
                  data-testid={`encabezado-${campo}`}
                  className="mt-1.5 text-[13px]"
                  minRows={2}
                  value={valores[campo]}
                  onChange={(e) => cambiar(campo, e.target.value)}
                  onBlur={() => validarCampo(campo)}
                  aria-invalid={!!errores[campo]}
                  required
                />
                <FieldError error={errores[campo]} />
              </>
            ) : (
              <p className="mt-1.5 text-[13px] text-foreground" data-testid={`encabezado-${campo}-valor`}>
                {plan[campo] || '—'}
              </p>
            )}
          </div>
        ))}
      </div>
      {error && (
        <p className="px-4 pb-3 text-[12.5px] text-destructive" data-testid="encabezado-error">
          {error}
        </p>
      )}
      {editando && (
        <div className="surface-divider flex justify-end gap-2 border-t bg-secondary/40 px-4 py-3">
          <Button variant="outline" size="sm" onClick={() => setEditando(false)}>
            {t('planTrabajo.encabezado.cancelar')}
          </Button>
          <Button size="sm" data-testid="encabezado-guardar" disabled={editar.isPending} onClick={() => void guardar()}>
            {editar.isPending ? t('common.saving') : t('planTrabajo.encabezado.guardar')}
          </Button>
        </div>
      )}
    </div>
  )
}

function Firmas({businessId, plan}: {businessId: string; plan: PlanTrabajo}) {
  const {t} = useTranslation()
  const capabilities = useCapabilities(businessId)
  const firmar = useFirmarPlan(businessId, plan.id)
  const adenda = useCrearAdenda(businessId, plan.id)
  const [error, setError] = useState<string | null>(null)
  const firmado = plan.estado === 'firmado'

  function firmarConError(rol: 'responsable' | 'representante') {
    setError(null)
    firmar.mutate(rol, {onError: (err) => setError(translateApiError(err))})
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="firmas">
      <FirmaBloque
        titulo={t('planTrabajo.firma.responsable')}
        firmadoAt={plan.firmado_responsable_at}
        testId="firma-responsable">
        {!plan.firmado_responsable_at && !firmado && (
          <>
            <Button
              size="sm"
              data-testid="firmar-responsable"
              disabled={!capabilities.sgsst_firmar || firmar.isPending}
              onClick={() => firmarConError('responsable')}>
              {t('planTrabajo.firma.firmar')}
            </Button>
            {!capabilities.sgsst_firmar && capabilities.firmar_bloqueado_por && (
              <p className="mt-1 text-[12px] text-muted-foreground" data-testid="firma-bloqueada">
                {capabilities.firmar_bloqueado_por}
              </p>
            )}
          </>
        )}
      </FirmaBloque>

      <FirmaBloque
        titulo={t('planTrabajo.firma.representante')}
        firmadoAt={plan.firmado_representante_at}
        testId="firma-representante">
        {!plan.firmado_representante_at && !firmado && (
          <Button
            size="sm"
            data-testid="firmar-representante"
            disabled={firmar.isPending}
            onClick={() => firmarConError('representante')}>
            {t('planTrabajo.firma.firmar')}
          </Button>
        )}
      </FirmaBloque>

      {firmado && (
        <div className="sm:col-span-2">
          <Button
            variant="outline"
            data-testid="crear-adenda"
            disabled={adenda.isPending}
            onClick={() => {
              setError(null)
              adenda.mutate(undefined, {onError: (err) => setError(translateApiError(err))})
            }}>
            {t('planTrabajo.adenda.crear')}
          </Button>
          <Hint className="mt-1">{t('planTrabajo.adenda.ayuda')}</Hint>
        </div>
      )}

      {error && (
        <p className="text-[12.5px] text-destructive sm:col-span-2" data-testid="firmas-error">
          {error}
        </p>
      )}
    </div>
  )
}

function FirmaBloque({
  titulo,
  firmadoAt,
  testId,
  children,
}: {
  titulo: string
  firmadoAt: string | null
  testId: string
  children: React.ReactNode
}) {
  const {t} = useTranslation()
  return (
    <div
      className={cn(
        'surface overflow-hidden border-l-[3px]',
        firmadoAt ? 'border-l-primary' : 'border-l-tone-amber-fg'
      )}
      data-testid={testId}>
      <div className="surface-divider border-b px-4 py-2.5">
        <MicroLabel>{titulo}</MicroLabel>
      </div>
      <div className="px-4 py-3.5">
        {firmadoAt ? (
          <p className="text-[13px] text-foreground" data-testid={`${testId}-hecha`}>
            {t('planTrabajo.firma.firmadaEl', {date: firmadoAt.slice(0, 10)})}
          </p>
        ) : (
          <div className="space-y-1.5">
            <p className="text-[13px] text-muted-foreground">{t('planTrabajo.firma.pendiente')}</p>
            {children}
          </div>
        )}
      </div>
    </div>
  )
}
